import React, { useState, useEffect, useRef } from 'react';
import { useNavigate, Link, useLocation } from 'react-router-dom';
import axios from 'axios';
import { Toaster } from 'react-hot-toast';

const Layout = ({ children, title, subtitle, headerExtras }) => {
  const navigate = useNavigate();
  const location = useLocation();
  const dropdownRef = useRef(null);

  const [user, setUser] = useState(null);
  const [isProfileOpen, setIsProfileOpen] = useState(false);
  const [isSettingsModalOpen, setIsSettingsModalOpen] = useState(false);
  const [loading, setLoading] = useState(true);
  const [profileData, setProfileData] = useState({ full_name: '', nick_name: '', email: '', profile_image: '', password: '' });
  const [isSavingProfile, setIsSavingProfile] = useState(false);
  const [showPassword, setShowPassword] = useState(false);

  // Accordion states
  const [isMasterDataOpen, setIsMasterDataOpen] = useState(location.pathname.includes('/clients') || location.pathname.includes('/sub-agents'));
  const [isMastersOpen, setIsMastersOpen] = useState(location.pathname.includes('/category-master') || location.pathname.includes('/templates') || location.pathname.includes('/add-category') || location.pathname.includes('/edit-category') || location.pathname.includes('/data-sources') || location.pathname.includes('/business-categories') || location.pathname.includes('/margin-setting'));
  const [isReportsOpen, setIsReportsOpen] = useState(location.pathname.includes('/hospital-report') || location.pathname.includes('/financial-statement') || location.pathname.includes('/working-capital') || location.pathname.includes('/project-percentage'));
  const [isAdminOpen, setIsAdminOpen] = useState(location.pathname.includes('/users') || location.pathname.includes('/user-groups') || location.pathname.includes('/user-logs'));
  const [isProjectsOpen, setIsProjectsOpen] = useState(location.pathname.includes('/hospital-report') || location.pathname.includes('/backup'));

  // Projects list for sidebar

  const [isSidebarHovered, setIsSidebarHovered] = useState(false);
  const isExpanded = isSidebarHovered || isMasterDataOpen || isMastersOpen || isReportsOpen || isAdminOpen || isProjectsOpen;

  useEffect(() => {
    const handleClickOutside = (event) => {
      if (dropdownRef.current && !dropdownRef.current.contains(event.target)) {
        setIsProfileOpen(false);
      }
    };
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);
  useEffect(() => {
    if (location.pathname.includes('/clients') || location.pathname.includes('/sub-agents')) setIsMasterDataOpen(true);
    if (location.pathname.includes('/category-master') || location.pathname.includes('/templates') || location.pathname.includes('/add-category') || location.pathname.includes('/edit-category') || location.pathname.includes('/data-sources') || location.pathname.includes('/business-categories') || location.pathname.includes('/margin-setting')) setIsMastersOpen(true);
    if (location.pathname.includes('/hospital-report') || location.pathname.includes('/financial-statement') || location.pathname.includes('/working-capital') || location.pathname.includes('/project-percentage')) setIsReportsOpen(true);
    if (location.pathname.includes('/users') || location.pathname.includes('/user-groups') || location.pathname.includes('/user-logs')) setIsAdminOpen(true);
    if (location.pathname.includes('/hospital-report') || location.pathname.includes('/backup')) setIsProjectsOpen(true);
  }, [location.pathname]);



  useEffect(() => {
    const fetchUser = async () => {
      try {
        const userRes = await axios.get(`${process.env.REACT_APP_API_URL}/home`, { withCredentials: true });
        if (userRes.status === 200 && userRes.data.user) {
          setUser(userRes.data.user);
          localStorage.setItem('user', userRes.data.user.user_name);

          // Fetch full profile
          const profileRes = await axios.get(`${process.env.REACT_APP_API_URL}/api/profile`, { withCredentials: true });
          if (profileRes.data) {
            setProfileData({ ...profileRes.data, password: '' });
            setUser(prev => ({ ...prev, profile_image: profileRes.data.profile_image }));
          }
        } else {
          navigate('/login');
        }
      } catch (err) {
        navigate('/login');
      } finally {
        setLoading(false);
      }
    };
    fetchUser();
  }, [navigate]);

  const handleLogout = async () => {
    try {
      await axios.post(`${process.env.REACT_APP_API_URL}/logout`, {}, { withCredentials: true });
      localStorage.removeItem('user');
      navigate('/login');
    } catch (err) {
      localStorage.removeItem('user');
      navigate('/login');
    }
  };

  const getInitials = (name) => {
    if (!name) return 'U';
    return name.slice(0, 2).toUpperCase();
  };

  const handleImageUpload = (e) => {
    const file = e.target.files[0];
    if (file) {
      if (file.size > 2 * 1024 * 1024) {
        import('react-hot-toast').then(({ toast }) => toast.error('Image must be less than 2MB'));
        return;
      }
      const reader = new FileReader();
      reader.onloadend = () => {
        setProfileData({ ...profileData, profile_image: reader.result });
      };
      reader.readAsDataURL(file);
    }
  };

  const saveProfile = async () => {
    setIsSavingProfile(true);
    try {
      await axios.put(`${process.env.REACT_APP_API_URL}/api/profile`, profileData, { withCredentials: true });
      setUser(prev => ({ ...prev, profile_image: profileData.profile_image }));
      import('react-hot-toast').then(({ toast }) => toast.success('Profile updated successfully!'));
      setProfileData(prev => ({ ...prev, password: '' })); // clear password after save
      setIsSettingsModalOpen(false);
    } catch (err) {
      import('react-hot-toast').then(({ toast }) => toast.error('Failed to update profile'));
    } finally {
      setIsSavingProfile(false);
    }
  };

  const isActive = (path) => location.pathname === path;

  if (loading) {
    return (
      <div className="min-h-screen flex flex-col items-center justify-center bg-slate-950 text-white">
        <div className="relative w-20 h-20">
          <div className="absolute inset-0 rounded-full border-4 border-emerald-500/20 border-t-emerald-400 animate-spin"></div>
        </div>
      </div>
    );
  }

  return (
    <div className="h-screen bg-slate-950 text-slate-100 font-sans flex overflow-hidden">
      {/* LEFT SIDEBAR */}
      <aside
        onMouseEnter={() => setIsSidebarHovered(true)}
        onMouseLeave={() => setIsSidebarHovered(false)}
        className={`fixed left-4 top-4 bottom-4 z-50 bg-[#0B1120]/80 backdrop-blur-2xl border border-white/[0.08] flex flex-col justify-between hidden md:flex rounded-3xl shadow-[0_8px_32px_rgba(0,0,0,0.5)] transition-all duration-500 ease-in-out ${isExpanded ? 'w-64' : 'w-20'
          }`}
      >
        <div>
          {/* Logo Brand */}
          <div
            onClick={() => { window.location.href = '/'; }}
            className="p-4 flex items-center gap-3 border-b border-white/[0.02] cursor-pointer hover:bg-white/[0.02] transition-colors rounded-t-3xl justify-center md:justify-start"
            title="Refresh Page"
          >
            <div className="w-10 h-10 rounded-xl bg-gradient-to-br from-emerald-400 via-teal-500 to-indigo-600 flex items-center justify-center shadow-[0_0_20px_rgba(16,185,129,0.3)] relative overflow-hidden group shrink-0">
              <div className="absolute inset-0 bg-white/20 group-hover:translate-x-full transition-transform duration-700 ease-out -skew-x-12"></div>
              <span className="font-black text-black text-xl relative z-10">Σ</span>
            </div>
            <div className={`transition-all duration-500 ease-in-out overflow-hidden whitespace-nowrap ${isExpanded ? 'opacity-100 max-w-[150px]' : 'opacity-0 max-w-0'}`}>
              <h1 className="font-extrabold text-xl tracking-tight bg-gradient-to-r from-emerald-300 via-teal-200 to-indigo-300 bg-clip-text text-transparent drop-shadow-sm">
                Bank Proposal
              </h1>
              <p className="text-[9px] text-slate-500 font-medium tracking-widest uppercase mt-0.5">Enterprise Edition</p>
            </div>
          </div>

          {/* Navigation Links */}
          <nav className="px-3 py-6 space-y-3 overflow-y-auto">

            <Link
              to="/"
              className={`transition-all duration-300 ease-in-out flex items-center rounded-full border cursor-pointer ${isExpanded
                ? 'w-full h-12 px-4 gap-3 bg-slate-900/40 border-white/[0.05] justify-start'
                : 'w-12 h-12 justify-center mx-auto bg-slate-900/40 border-white/[0.05]'
                } ${isActive('/')
                  ? 'text-indigo-400 bg-indigo-500/10 border-indigo-500/50 shadow-[0_0_15px_rgba(99,102,241,0.15)]'
                  : 'text-slate-400 hover:text-indigo-300 hover:bg-indigo-500/5 hover:border-indigo-500/20'
                }`}
            >
              <svg className="w-5 h-5 shrink-0" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M4 6a2 2 0 012-2h2a2 2 0 012 2v4a2 2 0 01-2 2H6a2 2 0 01-2-2V6zM14 6a2 2 0 012-2h2a2 2 0 012 2v4a2 2 0 01-2 2h-2a2 2 0 01-2-2V6zM4 16a2 2 0 012-2h2a2 2 0 012 2v4a2 2 0 01-2 2H6a2 2 0 01-2-2v-4zM14 16a2 2 0 012-2h2a2 2 0 012 2v4a2 2 0 01-2 2h-2a2 2 0 01-2-2v-4z" /></svg>
              {isExpanded && <span className="font-semibold text-sm whitespace-nowrap overflow-hidden transition-all duration-300">Dashboard</span>}
            </Link>


            {/* Projects Section - Dynamic from DB */}
            <div className="space-y-1">
              <button
                onClick={() => setIsProjectsOpen(!isProjectsOpen)}
                className={`transition-all duration-300 ease-in-out flex items-center rounded-full border cursor-pointer ${isExpanded
                  ? 'w-full h-12 px-4 gap-3 bg-slate-900/40 border-white/[0.05] justify-start'
                  : 'w-12 h-12 justify-center mx-auto bg-slate-900/40 border-white/[0.05]'
                  } ${(location.pathname.includes('/hospital-report') || location.pathname.includes('/backup'))
                    ? 'text-rose-400 bg-rose-500/10 border-rose-500/50 shadow-[0_0_15px_rgba(244,63,94,0.15)]'
                    : 'text-slate-400 hover:text-rose-300 hover:bg-rose-500/5 hover:border-rose-500/20'
                  }`}
              >
                <svg className="w-5 h-5 shrink-0" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M3 7v10a2 2 0 002 2h14a2 2 0 002-2V9a2 2 0 00-2-2h-6l-2-2H5a2 2 0 00-2 2z" /></svg>
                {isExpanded && <span className="font-semibold text-sm whitespace-nowrap overflow-hidden transition-all duration-300">Projects</span>}
                {isExpanded && (
                  <svg className={`w-3.5 h-3.5 text-slate-500 transition-transform duration-300 ml-auto shrink-0 ${isProjectsOpen ? 'rotate-180' : ''}`} fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M19 9l-7 7-7-7" /></svg>
                )}
              </button>

              <div className={`grid transition-all duration-300 ease-in-out ${isProjectsOpen && isExpanded ? 'grid-rows-[1fr] opacity-100 mt-2' : 'grid-rows-[0fr] opacity-0 pointer-events-none'}`}>
                <div className="overflow-hidden space-y-1 pl-4 border-l border-rose-500/30 ml-6 mr-2">
                  {/* New Project Button */}
                  <Link
                    to="/hospital-report"
                    onClick={(e) => {
                      if (isActive('/hospital-report')) {
                        e.preventDefault();
                        window.location.reload();
                      }
                    }}
                    className={`flex items-center gap-3 py-2 px-3 rounded-full transition-all duration-300 group ${location.pathname === '/hospital-report' && !window.location.search
                      ? 'text-rose-400 bg-rose-500/5 font-semibold'
                      : 'text-slate-400 hover:text-white hover:bg-white/5'
                      }`}
                  >
                    <svg className="w-3.5 h-3.5" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M12 6v6m0 0v6m0-6h6m-6 0H6" /></svg>
                    <span className="font-medium text-[13px]">All Proposal</span>
                  </Link>
                  <Link
                    to="/backup"
                    onClick={(e) => {
                      if (isActive('/backup')) {
                        e.preventDefault();
                        window.location.reload();
                      }
                    }}
                    className={`flex items-center gap-3 py-2 px-3 rounded-full transition-all duration-300 group ${location.pathname === '/backup'
                      ? 'text-rose-400 bg-rose-500/5 font-semibold'
                      : 'text-slate-400 hover:text-white hover:bg-white/5'
                      }`}
                  >
                    <svg className="w-3.5 h-3.5" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M5 8h14M5 8a2 2 0 110-4h14a2 2 0 110 4M5 8v10a2 2 0 002 2h10a2 2 0 002-2V8m-9 4h4" /></svg>
                    <span className="font-medium text-[13px]">Archival</span>
                  </Link>

                </div>
              </div>
            </div>

            {/* Client Section */}
            <div className="space-y-1">
              <button
                onClick={() => setIsMasterDataOpen(!isMasterDataOpen)}
                className={`transition-all duration-300 ease-in-out flex items-center rounded-full border cursor-pointer ${isExpanded
                  ? 'w-full h-12 px-4 gap-3 bg-slate-900/40 border-white/[0.05] justify-start'
                  : 'w-12 h-12 justify-center mx-auto bg-slate-900/40 border-white/[0.05]'
                  } ${location.pathname.includes('/clients') || location.pathname.includes('/sub-agents')
                    ? 'text-teal-400 bg-teal-500/10 border-teal-500/50 shadow-[0_0_15px_rgba(20,184,166,0.15)]'
                    : 'text-slate-400 hover:text-teal-300 hover:bg-teal-500/5 hover:border-teal-500/20'
                  }`}
              >
                <svg className="w-5 h-5 shrink-0" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M17 20h5v-2a3 3 0 00-5.356-1.857M17 20H7m10 0v-2c0-.656-.126-1.283-.356-1.857M7 20H2v-2a3 3 0 015.356-1.857M7 20v-2c0-.656.126-1.283.356-1.857m0 0a5.002 5.002 0 019.288 0M15 7a3 3 0 11-6 0 3 3 0 016 0zm6 3a2 2 0 11-4 0 2 2 0 014 0zM7 10a2 2 0 11-4 0 2 2 0 014 0z" /></svg>
                {isExpanded && <span className="font-semibold text-sm whitespace-nowrap overflow-hidden transition-all duration-300">Client</span>}
                {isExpanded && (
                  <svg className={`w-3.5 h-3.5 text-slate-500 transition-transform duration-300 ml-auto shrink-0 ${isMasterDataOpen ? 'rotate-180' : ''}`} fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M19 9l-7 7-7-7" /></svg>
                )}
              </button>

              <div className={`grid transition-all duration-300 ease-in-out ${isMasterDataOpen && isExpanded ? 'grid-rows-[1fr] opacity-100 mt-2' : 'grid-rows-[0fr] opacity-0 pointer-events-none'}`}>
                <div className="overflow-hidden space-y-1 pl-4 border-l border-teal-500/30 ml-6 mr-2">
                  <Link
                    to="/clients"
                    onClick={(e) => {
                      if (isActive('/clients')) {
                        e.preventDefault();
                        window.location.reload();
                      }
                    }} className={`flex items-center gap-3 py-2 px-3 rounded-full transition-all duration-300 group ${isActive('/clients') ? 'text-teal-400 bg-teal-500/5 font-semibold' : 'text-slate-400 hover:text-white hover:bg-white/5'}`}>
                    <svg className={`w-3.5 h-3.5 transition-colors ${isActive('/clients') ? 'drop-shadow-[0_0_8px_rgba(20,184,166,0.6)]' : 'group-hover:text-slate-300'}`} fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M19 21V5a2 2 0 00-2-2H7a2 2 0 00-2 2v16m14 0h2m-2 0h-5m-9 0H3m2 0h5M9 7h1m-1 4h1m4-4h1m-1 4h1m-5 10v-5a1 1 0 011-1h2a1 1 0 011 1v5m-4 0h4" /></svg>
                    <span className="font-medium text-[13px]">Clients</span>
                  </Link>
                  <Link
                    to="/sub-agents"
                    onClick={(e) => {
                      if (isActive('/sub-agents')) {
                        e.preventDefault();
                        window.location.reload();
                      }
                    }} className={`flex items-center gap-3 py-2 px-3 rounded-full transition-all duration-300 group ${isActive('/sub-agents') ? 'text-teal-400 bg-teal-500/5 font-semibold' : 'text-slate-400 hover:text-white hover:bg-white/5'}`}>
                    <svg className={`w-3.5 h-3.5 transition-colors ${isActive('/sub-agents') ? 'drop-shadow-[0_0_8px_rgba(20,184,166,0.6)]' : 'group-hover:text-slate-300'}`} fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M17 20h5v-2a3 3 0 00-5.356-1.857M17 20H7m10 0v-2c0-.656-.126-1.283-.356-1.857M7 20H2v-2a3 3 0 015.356-1.857M7 20v-2c0-.656.126-1.283.356-1.857m0 0a5.002 5.002 0 019.288 0M15 7a3 3 0 11-6 0 3 3 0 016 0zm6 3a2 2 0 11-4 0 2 2 0 014 0zM7 10a2 2 0 11-4 0 2 2 0 014 0z" /></svg>
                    <span className="font-medium text-[13px]">Sub Agents</span>
                  </Link>
                </div>
              </div>
            </div>

            {/* Masters Section */}
            <div className="space-y-1">
              <button
                onClick={() => setIsMastersOpen(!isMastersOpen)}
                className={`transition-all duration-300 ease-in-out flex items-center rounded-full border cursor-pointer ${isExpanded
                  ? 'w-full h-12 px-4 gap-3 bg-slate-900/40 border-white/[0.05] justify-start'
                  : 'w-12 h-12 justify-center mx-auto bg-slate-900/40 border-white/[0.05]'
                  } ${location.pathname.includes('/category-master') || location.pathname.includes('/templates') || location.pathname.includes('/add-category') || location.pathname.includes('/edit-category')
                    ? 'text-amber-400 bg-amber-500/10 border-amber-500/50 shadow-[0_0_15px_rgba(245,158,11,0.15)]'
                    : 'text-slate-400 hover:text-amber-300 hover:bg-amber-500/5 hover:border-amber-500/20'
                  }`}
              >
                <svg className="w-5 h-5 shrink-0" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M19 11H5m14 0a2 2 0 012 2v6a2 2 0 01-2 2H5a2 2 0 01-2-2v-6a2 2 0 012-2m14 0V9a2 2 0 00-2-2M5 11V9a2 2 0 002-2m0 0V5a2 2 0 012-2h6a2 2 0 012 2v2M7 7h10" /></svg>
                {isExpanded && <span className="font-semibold text-sm whitespace-nowrap overflow-hidden transition-all duration-300">Masters</span>}
                {isExpanded && (
                  <svg className={`w-3.5 h-3.5 text-slate-500 transition-transform duration-300 ml-auto shrink-0 ${isMastersOpen ? 'rotate-180' : ''}`} fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M19 9l-7 7-7-7" /></svg>
                )}
              </button>

              <div className={`grid transition-all duration-300 ease-in-out ${isMastersOpen && isExpanded ? 'grid-rows-[1fr] opacity-100 mt-2' : 'grid-rows-[0fr] opacity-0 pointer-events-none'}`}>
                <div className="overflow-hidden space-y-1 pl-4 border-l border-amber-500/30 ml-6 mr-2">
                  <Link
                    to="/category-master"
                    onClick={(e) => {
                      if (isActive('/category-master')) {
                        e.preventDefault();
                        window.location.reload();
                      }
                    }} className={`flex items-center gap-3 py-2 px-3 rounded-full transition-all duration-300 group ${(isActive('/category-master') || location.pathname.includes('/add-category') || location.pathname.includes('/edit-category')) ? 'text-amber-400 bg-amber-500/5 font-semibold' : 'text-slate-400 hover:text-white hover:bg-white/5'}`}>
                    <svg className={`w-3.5 h-3.5 transition-colors ${(isActive('/category-master') || location.pathname.includes('/add-category') || location.pathname.includes('/edit-category')) ? 'drop-shadow-[0_0_8px_rgba(245,158,11,0.6)]' : 'group-hover:text-slate-300'}`} fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M4 6a2 2 0 012-2h2a2 2 0 012 2v4a2 2 0 01-2 2H6a2 2 0 01-2-2V6zM14 6a2 2 0 012-2h2a2 2 0 012 2v4a2 2 0 01-2 2h-2a2 2 0 01-2-2V6zM4 16a2 2 0 012-2h2a2 2 0 012 2v4a2 2 0 01-2 2H6a2 2 0 01-2-2v-4zM14 16a2 2 0 012-2h2a2 2 0 012 2v4a2 2 0 01-2 2h-2a2 2 0 01-2-2v-4z" /></svg>
                    <span className="font-medium text-[13px]">Category </span>
                  </Link>
                  <Link
                    to="/templates"
                    onClick={(e) => {
                      if (isActive('/templates')) {
                        e.preventDefault();
                        window.location.reload();
                      }
                    }} className={`flex items-center gap-3 py-2 px-3 rounded-full transition-all duration-300 group ${isActive('/templates') ? 'text-amber-400 bg-amber-500/5 font-semibold' : 'text-slate-400 hover:text-white hover:bg-white/5'}`}>
                    <svg className={`w-3.5 h-3.5 transition-colors ${isActive('/templates') ? 'drop-shadow-[0_0_8px_rgba(245,158,11,0.6)]' : 'group-hover:text-slate-300'}`} fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M19 11H5m14 0a2 2 0 012 2v6a2 2 0 01-2 2H5a2 2 0 01-2-2v-6a2 2 0 012-2m14 0V9a2 2 0 00-2-2M5 11V9a2 2 0 002-2m0 0V5a2 2 0 012-2h6a2 2 0 012 2v2M7 7h10" /></svg>
                    <span className="font-medium text-[13px]">Templates </span>
                  </Link>
                  <Link
                    to="/data-sources"
                    onClick={(e) => {
                      if (isActive('/data-sources')) {
                        e.preventDefault();
                        window.location.reload();
                      }
                    }} className={`flex items-center gap-3 py-2 px-3 rounded-full transition-all duration-300 group ${isActive('/data-sources') ? 'text-amber-400 bg-amber-500/5 font-semibold' : 'text-slate-400 hover:text-white hover:bg-white/5'}`}>
                    <svg className={`w-3.5 h-3.5 transition-colors ${isActive('/data-sources') ? 'drop-shadow-[0_0_8px_rgba(245,158,11,0.6)]' : 'group-hover:text-slate-300'}`} fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M4 7v10c0 2.21 3.582 4 8 4s8-1.79 8-4V7M4 7c0 2.21 3.582 4 8 4s8-1.79 8-4M4 7c0-2.21 3.582-4 8-4s8 1.79 8 4m0 5c0 2.21-3.582 4-8 4s-8-1.79-8-4" /></svg>
                    <span className="font-medium text-[13px]">Data Source </span>
                  </Link>
                  <Link
                    to="/business-categories"
                    onClick={(e) => {
                      if (isActive('/business-categories')) {
                        e.preventDefault();
                        window.location.reload();
                      }
                    }} className={`flex items-center gap-3 py-2 px-3 rounded-full transition-all duration-300 group ${isActive('/business-categories') ? 'text-amber-400 bg-amber-500/5 font-semibold' : 'text-slate-400 hover:text-white hover:bg-white/5'}`}>
                    <svg className={`w-3.5 h-3.5 transition-colors ${isActive('/business-categories') ? 'drop-shadow-[0_0_8px_rgba(245,158,11,0.6)]' : 'group-hover:text-slate-300'}`} fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M21 13.255A23.931 23.931 0 0112 15c-3.183 0-6.22-.62-9-1.745M16 6V4a2 2 0 00-2-2h-4a2 2 0 00-2 2v2m4 6h.01M5 20h14a2 2 0 002-2V8a2 2 0 00-2-2H5a2 2 0 00-2 2v10a2 2 0 002 2z" /></svg>
                    <span className="font-medium text-[13px]">Business Categories</span>
                  </Link>
                  <Link
                    to="/margin-setting"
                    onClick={(e) => {
                      if (isActive('/margin-setting')) {
                        e.preventDefault();
                        window.location.reload();
                      }
                    }} className={`flex items-center gap-3 py-2 px-3 rounded-full transition-all duration-300 group ${isActive('/margin-setting') ? 'text-amber-400 bg-amber-500/5 font-semibold' : 'text-slate-400 hover:text-white hover:bg-white/5'}`}>
                    <svg className={`w-3.5 h-3.5 transition-colors ${isActive('/margin-setting') ? 'drop-shadow-[0_0_8px_rgba(245,158,11,0.6)]' : 'group-hover:text-slate-300'}`} fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M21 13.255A23.931 23.931 0 0112 15c-3.183 0-6.22-.62-9-1.745M16 6V4a2 2 0 00-2-2h-4a2 2 0 00-2 2v2m4 6h.01M5 20h14a2 2 0 002-2V8a2 2 0 00-2-2H5a2 2 0 00-2 2v10a2 2 0 002 2z" /></svg>
                    <span className="font-medium text-[13px]">Project Percentage</span>
                  </Link>
                </div>
              </div>
            </div>

            {/* Administration Section */}
            <div className="space-y-1">
              <button
                onClick={() => setIsAdminOpen(!isAdminOpen)}
                className={`transition-all duration-300 ease-in-out flex items-center rounded-full border cursor-pointer ${isExpanded
                  ? 'w-full h-12 px-4 gap-3 bg-slate-900/40 border-white/[0.05] justify-start'
                  : 'w-12 h-12 justify-center mx-auto bg-slate-900/40 border-white/[0.05]'
                  } ${location.pathname.includes('/users') || location.pathname.includes('/user-groups') || location.pathname.includes('/user-logs')
                    ? 'text-violet-400 bg-violet-500/10 border-violet-500/50 shadow-[0_0_15px_rgba(139,92,246,0.15)]'
                    : 'text-slate-400 hover:text-violet-300 hover:bg-violet-500/5 hover:border-violet-500/20'
                  }`}
              >
                <svg className="w-5 h-5 shrink-0" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M16 7a4 4 0 11-8 0 4 4 0 018 0zM12 14a7 7 0 00-7 7h14a7 7 0 00-7-7z" /></svg>
                {isExpanded && <span className="font-semibold text-sm whitespace-nowrap overflow-hidden transition-all duration-300">Administration</span>}
                {isExpanded && (
                  <svg className={`w-3.5 h-3.5 text-slate-500 transition-transform duration-300 ml-auto shrink-0 ${isAdminOpen ? 'rotate-180' : ''}`} fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M19 9l-7 7-7-7" /></svg>
                )}
              </button>

              <div className={`grid transition-all duration-300 ease-in-out ${isAdminOpen && isExpanded ? 'grid-rows-[1fr] opacity-100 mt-2' : 'grid-rows-[0fr] opacity-0 pointer-events-none'}`}>
                <div className="overflow-hidden space-y-1 pl-4 border-l border-violet-500/30 ml-6 mr-2">
                  <Link
                    to="/users"
                    onClick={(e) => {
                      if (isActive('/users')) {
                        e.preventDefault();
                        window.location.reload();
                      }
                    }} className={`flex items-center gap-3 py-2 px-3 rounded-full transition-all duration-300 group ${isActive('/users') ? 'text-violet-400 bg-violet-500/5 font-semibold' : 'text-slate-400 hover:text-white hover:bg-white/5'}`}>
                    <svg className={`w-3.5 h-3.5 transition-colors ${isActive('/users') ? 'drop-shadow-[0_0_8px_rgba(139,92,246,0.6)]' : 'group-hover:text-slate-300'}`} fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M16 7a4 4 0 11-8 0 4 4 0 018 0zM12 14a7 7 0 00-7 7h14a7 7 0 00-7-7z" /></svg>
                    <span className="font-medium text-[13px]">Users</span>
                  </Link>
                  <Link
                    to="/user-groups"
                    onClick={(e) => {
                      if (isActive('/user-groups')) {
                        e.preventDefault();
                        window.location.reload();
                      }
                    }} className={`flex items-center gap-3 py-2 px-3 rounded-full transition-all duration-300 group ${isActive('/user-groups') ? 'text-violet-400 bg-violet-500/5 font-semibold' : 'text-slate-400 hover:text-white hover:bg-white/5'}`}>
                    <svg className={`w-3.5 h-3.5 transition-colors ${isActive('/user-groups') ? 'drop-shadow-[0_0_8px_rgba(139,92,246,0.6)]' : 'group-hover:text-slate-300'}`} fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M12 15v2m-6 4h12a2 2 0 002-2v-6a2 2 0 00-2-2H6a2 2 0 00-2 2v6a2 2 0 022 2zm10-10V7a4 4 0 00-8 0v4h8z" /></svg>
                    <span className="font-medium text-[13px]">User Groups</span>
                  </Link>
                  <Link
                    to="/user-logs"
                    onClick={(e) => {
                      if (isActive('/user-logs')) {
                        e.preventDefault();
                        window.location.reload();
                      }
                    }} className={`flex items-center gap-3 py-2 px-3 rounded-full transition-all duration-300 group ${isActive('/user-logs') ? 'text-violet-400 bg-violet-500/5 font-semibold' : 'text-slate-400 hover:text-white hover:bg-white/5'}`}>
                    <svg className={`w-3.5 h-3.5 transition-colors ${isActive('/user-logs') ? 'drop-shadow-[0_0_8px_rgba(139,92,246,0.6)]' : 'group-hover:text-slate-300'}`} fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z" /></svg>
                    <span className="font-medium text-[13px]">User Logs</span>
                  </Link>
                </div>
              </div>
            </div>
          </nav>
        </div>

        <div className="p-4 border-t border-white/[0.05] bg-slate-900/30 flex justify-center">
          <button
            onClick={handleLogout}
            className={`transition-all duration-300 ease-in-out flex items-center justify-center text-rose-400 hover:bg-rose-500/10 hover:text-rose-300 font-bold border border-rose-500/20 hover:border-rose-500/50 shadow-sm rounded-full ${isExpanded
              ? 'w-full px-4 py-2.5 text-xs gap-2'
              : 'w-10 h-10 p-0 text-center'
              }`}
            title="Logout System"
          >
            <svg className="w-4 h-4 shrink-0" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M17 16l4-4m0 0l-4-4m4 4H7m6 4v1a3 3 0 01-3 3H6a3 3 0 01-3-3V7a3 3 0 013-3h4a3 3 0 013 3v1" />
            </svg>
            {isExpanded && <span className="whitespace-nowrap overflow-hidden transition-all duration-300">LOGOUT SYSTEM</span>}
          </button>
        </div>
      </aside>

      {/* MAIN CONTENT AREA */}
      <main className={`flex-1 flex flex-col overflow-y-auto transition-all duration-500 pl-0 ${isExpanded ? 'md:pl-72' : 'md:pl-28'}`}>
        <header className="h-16 shrink-0 border-b border-slate-900 px-6 flex items-center justify-between !bg-slate-950 sticky top-0 z-50">
          <div>
            <h2 className="font-bold text-lg text-white">{title}</h2>
            {subtitle && <p className="text-xs text-gray-500">{subtitle}</p>}
          </div>

          <div className="flex items-center gap-4">
            {headerExtras}
            <div className="relative" ref={dropdownRef}>
              <button onClick={() => setIsProfileOpen(!isProfileOpen)} className="flex items-center gap-2 focus:outline-none">
                <div className="w-9 h-9 rounded-full bg-gradient-to-tr from-emerald-400 to-indigo-600 flex items-center justify-center text-black font-extrabold text-sm border-2 border-emerald-500/20 hover:scale-105 transition duration-300 overflow-hidden">
                  {user?.profile_image ? (
                    <img src={user.profile_image} alt="Profile" className="w-full h-full object-cover" />
                  ) : (
                    getInitials(user?.user_name)
                  )}
                </div>
              </button>

              {isProfileOpen && (
                <div className="absolute right-0 mt-3 w-56 rounded-2xl bg-slate-900 border border-slate-800/80 shadow-2xl backdrop-blur-xl p-2 z-50">
                  <div className="p-3 border-b border-slate-800">
                    <p className="text-xs text-gray-500">Signed in as</p>
                    <p className="font-bold text-white text-sm truncate">{user?.user_name}</p>
                  </div>
                  <div className="border-t border-slate-800/80 pt-1 mt-1">
                    <button onClick={() => { setIsSettingsModalOpen(true); setIsProfileOpen(false); }} className="w-full text-left px-3 py-2 rounded-xl text-xs text-slate-300 hover:bg-slate-800 transition flex items-center gap-2 font-medium mb-1">
                      <svg className="w-3.5 h-3.5" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M10.325 4.317c.426-1.756 2.924-1.756 3.35 0a1.724 1.724 0 002.573 1.066c1.543-.94 3.31.826 2.37 2.37a1.724 1.724 0 001.065 2.572c1.756.426 1.756 2.924 0 3.35a1.724 1.724 0 00-1.066 2.573c.94 1.543-.826 3.31-2.37 2.37a1.724 1.724 0 00-2.572 1.065c-.426 1.756-2.924 1.756-3.35 0a1.724 1.724 0 00-2.573-1.066c-1.543.94-3.31-.826-2.37-2.37a1.724 1.724 0 00-1.065-2.572c-1.756-.426-1.756-2.924 0-3.35a1.724 1.724 0 001.066-2.573c-.94-1.543.826-3.31 2.37-2.37.996.608 2.296.07 2.572-1.065z"></path><path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M15 12a3 3 0 11-6 0 3 3 0 016 0z"></path></svg>
                      Profile Settings
                    </button>
                    <button onClick={handleLogout} className="w-full text-left px-3 py-2 rounded-xl text-xs text-rose-400 hover:bg-rose-500/10 hover:text-rose-300 transition flex items-center gap-2 font-semibold">
                      <svg className="w-3.5 h-3.5" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M17 16l4-4m0 0l-4-4m4 4H7m6 4v1a3 3 0 01-3 3H6a3 3 0 01-3-3V7a3 3 0 013-3h4a3 3 0 013 3v1" /></svg>
                      Logout
                    </button>
                  </div>
                </div>
              )}
            </div>
          </div>
        </header>

        <div className="p-6 space-y-6 flex-1 w-full">
          {children}
        </div>
      </main>
      <Toaster
        position="top-right"
        toastOptions={{
          style: {
            background: '#1e293b',
            color: '#fff',
            border: '1px solid #334155',
          },
        }}
      />

      {/* Profile Settings Modal */}
      {isSettingsModalOpen && (
        <div className="fixed inset-0 bg-black/60 backdrop-blur-sm flex items-center justify-center z-[9999] p-4">
          <div className="bg-slate-900 border border-slate-800 rounded-2xl w-full max-w-md shadow-2xl overflow-hidden">
            <div className="p-5 border-b border-slate-800 flex justify-between items-center bg-slate-900/50">
              <h3 className="text-lg font-bold text-white">Profile Settings</h3>
              <button onClick={() => setIsSettingsModalOpen(false)} className="text-slate-400 hover:text-white transition">
                <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M6 18L18 6M6 6l12 12" /></svg>
              </button>
            </div>

            <div className="p-6 space-y-5">
              <div className="flex flex-col items-center gap-4">
                <div className="relative group">
                  <div className="w-24 h-24 rounded-full border-4 border-slate-800 overflow-hidden bg-slate-800 flex items-center justify-center">
                    {profileData.profile_image ? (
                      <img src={profileData.profile_image} alt="Profile" className="w-full h-full object-cover" />
                    ) : (
                      <span className="text-3xl text-slate-500 font-bold">{getInitials(user?.user_name)}</span>
                    )}
                  </div>
                  <label className="absolute inset-0 bg-black/50 flex items-center justify-center opacity-0 group-hover:opacity-100 transition-opacity cursor-pointer rounded-full overflow-hidden">
                    <svg className="w-6 h-6 text-white pointer-events-none" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M3 9a2 2 0 012-2h.93a2 2 0 001.664-.89l.812-1.22A2 2 0 0110.07 4h3.86a2 2 0 011.664.89l.812 1.22A2 2 0 0018.07 7H19a2 2 0 012 2v9a2 2 0 01-2 2H5a2 2 0 01-2-2V9z" /><path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M15 13a3 3 0 11-6 0 3 3 0 016 0z" /></svg>
                    <input type="file" accept="image/*" className="absolute inset-0 w-full h-full opacity-0 cursor-pointer" onChange={handleImageUpload} />
                  </label>
                </div>
                <p className="text-xs text-slate-500">Click image to upload (Max 2MB)</p>
              </div>

              <div className="space-y-4">
                <div>
                  <label className="block text-xs font-medium text-slate-400 mb-1">Full Name</label>
                  <input type="text" value={profileData.full_name || ''} onChange={e => setProfileData({ ...profileData, full_name: e.target.value })} className="w-full bg-slate-950 border border-slate-800 rounded-xl px-4 py-2.5 text-sm text-white focus:outline-none focus:border-indigo-500 focus:ring-1 focus:ring-indigo-500 transition-all" placeholder="John Doe" />
                </div>
                <div>
                  <label className="block text-xs font-medium text-slate-400 mb-1">Nick Name</label>
                  <input type="text" value={profileData.nick_name || ''} onChange={e => setProfileData({ ...profileData, nick_name: e.target.value })} className="w-full bg-slate-950 border border-slate-800 rounded-xl px-4 py-2.5 text-sm text-white focus:outline-none focus:border-indigo-500 focus:ring-1 focus:ring-indigo-500 transition-all" placeholder="Johnny" />
                </div>
                <div>
                  <label className="block text-xs font-medium text-slate-400 mb-1">Email</label>
                  <input type="email" value={profileData.email || ''} onChange={e => setProfileData({ ...profileData, email: e.target.value })} className="w-full bg-slate-950 border border-slate-800 rounded-xl px-4 py-2.5 text-sm text-white focus:outline-none focus:border-indigo-500 focus:ring-1 focus:ring-indigo-500 transition-all" placeholder="john@example.com" />
                </div>
                <div>
                  <label className="block text-xs font-medium text-slate-400 mb-1">New Password (Optional)</label>
                  <div className="relative">
                    <input type={showPassword ? "text" : "password"} value={profileData.password || ''} onChange={e => setProfileData({ ...profileData, password: e.target.value })} className="w-full bg-slate-950 border border-slate-800 rounded-xl px-4 py-2.5 pr-10 text-sm text-white focus:outline-none focus:border-indigo-500 focus:ring-1 focus:ring-indigo-500 transition-all" placeholder="Enter new password to update" />
                    <button type="button" onClick={() => setShowPassword(!showPassword)} className="absolute inset-y-0 right-3 flex items-center text-slate-500 hover:text-slate-300 transition-colors">
                      {showPassword ? (
                        <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M15 12a3 3 0 11-6 0 3 3 0 016 0z" /><path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M2.458 12C3.732 7.943 7.523 5 12 5c4.478 0 8.268 2.943 9.542 7-1.274 4.057-5.064 7-9.542 7-4.477 0-8.268-2.943-9.542-7z" /></svg>
                      ) : (
                        <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M13.875 18.825A10.05 10.05 0 0112 19c-4.478 0-8.268-2.943-9.543-7a9.97 9.97 0 011.563-3.029m5.858.908a3 3 0 114.243 4.243M9.878 9.878l4.242 4.242M9.88 9.88l-3.29-3.29m7.532 7.532l3.29 3.29M3 3l3.59 3.59m0 0A9.953 9.953 0 0112 5c4.478 0 8.268 2.943 9.543 7a10.025 10.025 0 01-4.132 5.411m0 0L21 21" /></svg>
                      )}
                    </button>
                  </div>
                </div>
              </div>
            </div>

            <div className="p-5 border-t border-slate-800 bg-slate-900/50 flex justify-end gap-3">
              <button onClick={() => setIsSettingsModalOpen(false)} className="px-4 py-2 rounded-xl text-sm font-medium text-slate-300 hover:text-white hover:bg-slate-800 transition-colors">
                Cancel
              </button>
              <button onClick={saveProfile} disabled={isSavingProfile} className="px-5 py-2 rounded-xl text-sm font-bold text-white bg-indigo-600 hover:bg-indigo-500 focus:ring-4 focus:ring-indigo-500/20 transition-all shadow-[0_0_15px_rgba(79,70,229,0.3)] disabled:opacity-50 flex items-center gap-2">
                {isSavingProfile ? (
                  <>
                    <svg className="animate-spin h-4 w-4 text-white" fill="none" viewBox="0 0 24 24"><circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle><path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path></svg>
                    Saving...
                  </>
                ) : 'Save Changes'}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default Layout;
